import { Box, Flex, Text } from '@mantine/core';
import { Fragment, useMemo } from 'react';
import {
  BORDER_COLOR,
  CELL_PADDING,
  FONT_SIZE,
  HEADER_BG,
  INDENT_SIZE,
  LINE_HEIGHT,
  TEXT_COLOR
} from '../tableConstants';
import { useCellNavigation } from '../useCellNavigation';
import { SingleFundHeaderRow } from '../components/SingleFundHeaderRow';
import { MultiFundRow } from '../components/MultiFundRow';
import { SingleFundRow } from '../components/SingleFundRow';
import { SeeMoreText } from '../components/SeeMoreText';
import { QuestionHeader } from '../components/QuestionHeader';
import { parseQuestionDisplayName } from '../tableUtils';
import { idCreator } from '../../../../../Project/utils/idCreator';

const makeGroupKey = (groupValue, subGroupValue = '') => `${groupValue}::${subGroupValue}`;

const getMultiFundValue = ({ groupDataByFund, fundKey, groupColumnValue, subGroupValue }) => {
  return groupDataByFund?.[fundKey]?.rowByGroupAndSub?.get(
    makeGroupKey(groupColumnValue, subGroupValue)
  );
};

export const GroupTurnoverTable = ({
  schema,
  fundId,
  isMultiFunds,
  funds,
  tableName,
  onChangeFund,
  questionLevels = [],
  groupColumn,
  rankLevels = [],
  subGroupColumn,
  commonColumn,
  name = '',
  isQuestionOpen = true,
  onToggleQuestion
}) => {
  const fundKeys = useMemo(() => Object.keys(funds || {}), [funds]);

  const { cellRefs, registerCell, onCellKeyDown } = useCellNavigation({ isMultiFunds, fundKeys });

  const groupDataByFund = useMemo(() => {
    return fundKeys.reduce((acc, key) => {
      const rows = funds?.[key]?.fund_data?.[tableName] || [];
      const rowByGroupAndSub = new Map();
      const indexByGroupAndSub = new Map();
      const rowsByGroup = new Map();
      const firstRowByGroup = new Map();

      rows.forEach((row, index) => {
        const groupValue = row?.[groupColumn];
        const subGroupValue = row?.[subGroupColumn];
        rowByGroupAndSub.set(makeGroupKey(groupValue, subGroupValue), row);
        indexByGroupAndSub.set(makeGroupKey(groupValue, subGroupValue), index);

        if (!rowsByGroup.has(groupValue)) {
          rowsByGroup.set(groupValue, []);
          firstRowByGroup.set(groupValue, row);
        }
        rowsByGroup.get(groupValue).push(row);
      });

      acc[key] = {
        rowByGroupAndSub,
        indexByGroupAndSub,
        rowsByGroup,
        firstRowByGroup
      };

      return acc;
    }, {});
  }, [fundKeys, funds, tableName, groupColumn, subGroupColumn]);

  const onCellChange = (column, value, _fundId, groupValue, subGroupValue) => {
    const targetFundId = _fundId || fundId;

    const currentTable = funds?.[targetFundId]?.fund_data?.[tableName] || [];
    let updatedTable = currentTable;
    if (subGroupValue) {
      const rowIndex =
        groupDataByFund?.[targetFundId]?.indexByGroupAndSub?.get(
          makeGroupKey(groupValue, subGroupValue)
        ) ?? -1;
      updatedTable = currentTable.map((row, index) =>
        index === rowIndex
          ? {
              ...row,
              [column]: value
            }
          : row
      );
    } else {
      updatedTable = currentTable.map(row =>
        row[groupColumn] === groupValue
          ? {
              ...row,
              [column]: value
            }
          : row
      );
    }

    onChangeFund(tableName, updatedTable, _fundId);
  };

  const { questionNumber, questionName } = parseQuestionDisplayName(name);

  if (isMultiFunds) {
    return (
      <Flex
        id={`formpf-group-turnover-table-${idCreator(tableName)}-multi`}
        direction="column"
        bd={`1px solid ${BORDER_COLOR}`}
      >
        <QuestionHeader
          id={`formpf-group-turnover-table-header-${idCreator(tableName)}-multi`}
          name={questionName}
          number={questionNumber}
          isMultiFunds={isMultiFunds}
          fundKeys={fundKeys}
          funds={funds}
          isOpen={isQuestionOpen}
          onToggle={onToggleQuestion}
        />

        {isQuestionOpen &&
          schema.properties[groupColumn].rules?.array?.enum.map((item, index) => {
            return (
              <Flex direction="column" key={`${item.value}-multi-view`}>
                {questionLevels.map((question, questionIndex) => {
                  const keyPrefix = question.level || question.name;

                  return (
                    <Fragment
                      key={`${tableName}-${index}-${item.value}-${questionIndex}-multi-view`}
                    >
                      {renderMultiFundsSimpleTable({
                        columns: question.columns,
                        children: question.children,
                        schema,
                        onCellChange,
                        groupDataByFund,
                        levelIndex: 0,
                        funds,
                        fundKeys,
                        tableName,
                        level: question.level || rankLevels?.[index],
                        name: item.label,
                        cellRefs: { current: cellRefs, register: registerCell },
                        onCellKeyDown,
                        keyPrefix,
                        dataIndex: index,
                        subGroupColumn,
                        commonColumn,
                        groupColumnValue: item.value
                      })}
                    </Fragment>
                  );
                })}
              </Flex>
            );
          })}
      </Flex>
    );
  }

  return (
    <Box
      id={`formpf-group-turnover-table-${idCreator(tableName)}-single`}
      bd={`1px solid ${BORDER_COLOR}`}
    >
      <QuestionHeader
        id={`formpf-group-turnover-table-header-${idCreator(tableName)}-single`}
        number={questionNumber}
        name={questionName}
        isMultiFunds={isMultiFunds}
        isOpen={isQuestionOpen}
        onToggle={onToggleQuestion}
      />
      {isQuestionOpen &&
        schema.properties[groupColumn]?.rules?.array?.enum.map((item, index) => {
          const rows = groupDataByFund?.[fundId]?.rowsByGroup?.get(item.value) || [];

          return (
            <Flex direction="column" key={item.value}>
              {questionLevels.map((question, questionIndex) => {
                const keyPrefix = question.level || question.name;

                return (
                  <Fragment key={`${tableName}-${index}-${item.value}-${questionIndex}`}>
                    {renderSimpleTable({
                      columns: question.columns,
                      children: question.children,
                      schema,
                      columnValues: rows || [],
                      onCellChange,
                      levelIndex: 0,
                      level: question.level || rankLevels?.[index],
                      name: item.label,
                      questionName: question.name,
                      dataIndex: index,
                      cellRefs: { current: cellRefs, register: registerCell },
                      onCellKeyDown,
                      keyPrefix,
                      fund: funds?.[fundId],
                      tableName,
                      subGroupColumn,
                      commonColumn,
                      groupColumnValue: item.value
                    })}
                  </Fragment>
                );
              })}
            </Flex>
          );
        })}
    </Box>
  );
};

const renderMultiFundsSimpleTable = ({
  columns,
  schema,
  onCellChange,
  groupDataByFund,
  levelIndex,
  funds,
  fundKeys,
  tableName,
  level,
  name,
  children,
  cellRefs,
  onCellKeyDown,
  keyPrefix,
  subGroupColumn,
  commonColumn,
  groupColumnValue
}) => {
  const columnSize = Object.keys(columns).length;
  return (
    <>
      {columnSize > 1 && (
        <Flex
          sx={() => ({
            borderBottom: `1px solid ${BORDER_COLOR}`,
            borderRight: `1px solid ${BORDER_COLOR}`
          })}
        >
          <Flex
            id={`formpf-group-turnover-multi-left-${idCreator(`${tableName}-${groupColumnValue}`)}`}
            flex="auto"
          >
            {levelIndex > 0 && (
              <Box
                id={`formpf-group-turnover-multi-indent-${idCreator(`${tableName}-${groupColumnValue}`)}`}
                maw={`${(levelIndex + 1) * INDENT_SIZE}px`}
                miw={`${(levelIndex + 1) * INDENT_SIZE}px`}
              ></Box>
            )}
            <Flex
              id={`formpf-group-turnover-multi-inner-${idCreator(`${tableName}-${groupColumnValue}`)}`}
              flex="auto"
              sx={() => ({
                border: `1px solid ${BORDER_COLOR}`,
                borderTop: 'none'
              })}
            >
              {level && (
                <Text
                  id={`formpf-group-turnover-multi-level-${idCreator(`${tableName}-${groupColumnValue}`)}`}
                  p={CELL_PADDING}
                  sx={() => ({
                    borderRight: `1px solid ${BORDER_COLOR}`
                  })}
                  bg={HEADER_BG}
                  miw={`${INDENT_SIZE}px`}
                  ta="center"
                  c={TEXT_COLOR}
                  fz={FONT_SIZE}
                  lh={LINE_HEIGHT}
                >
                  {level}
                </Text>
              )}

              <Text
                id={`formpf-group-turnover-multi-name-${idCreator(`${tableName}-${groupColumnValue}`)}`}
                p={CELL_PADDING}
                bg={HEADER_BG}
                flex="auto"
                c={TEXT_COLOR}
                fz={FONT_SIZE}
                lh={LINE_HEIGHT}
              >
                <SeeMoreText text={name} />
              </Text>
            </Flex>
          </Flex>
        </Flex>
      )}
      <MultiFundRow
        key={`${keyPrefix}-${commonColumn}-${groupColumnValue}`}
        columnSchema={columns[commonColumn]}
        cellSchema={schema.properties[commonColumn]}
        levelIndex={levelIndex}
        fundKeys={fundKeys}
        getValue={fundKey =>
          groupDataByFund?.[fundKey]?.firstRowByGroup?.get(groupColumnValue)?.[commonColumn]
        }
        onFundChange={(fundKey, value) =>
          onCellChange(commonColumn, value, fundKey, groupColumnValue, null)
        }
        cellRefs={cellRefs}
        getCellKey={fundKey => `${keyPrefix}-${commonColumn}-${groupColumnValue}-${fundKey}`}
        onCellKeyDown={onCellKeyDown}
        label={columns[commonColumn].name}
        columnLevel={columnSize === 1 ? level : undefined}
        funds={funds}
        tableName={tableName}
        fieldName={commonColumn}
        symbol={schema.properties[commonColumn]?.symbol}
      />
      {!children &&
        schema.properties[subGroupColumn]?.rules?.array?.enum
          ?.filter(item => item.value.includes(groupColumnValue))
          ?.map(item => (
            <Fragment key={item.value}>
              {Object.keys(columns)
                .filter(column => column !== commonColumn)
                .map(column => {
                  return (
                    <MultiFundRow
                      key={`${keyPrefix}-${column}-${groupColumnValue}-${item.value}`}
                      columnSchema={columns[subGroupColumn]}
                      cellSchema={schema.properties[column]}
                      levelIndex={levelIndex}
                      fundKeys={fundKeys}
                      getValue={fundKey =>
                        getMultiFundValue({
                          groupDataByFund,
                          fundKey,
                          groupColumnValue,
                          subGroupValue: item.value
                        })
                      }
                      onFundChange={(fundKey, value) =>
                        onCellChange(column, value, fundKey, groupColumnValue, item.value)
                      }
                      cellRefs={cellRefs}
                      getCellKey={fundKey =>
                        `${keyPrefix}-${column}-${groupColumnValue}-${item.value}-${fundKey}`
                      }
                      onCellKeyDown={onCellKeyDown}
                      label={`${item.label} (${columns[column].name})`}
                      columnLevel={columnSize === 1 ? level : undefined}
                      funds={funds}
                      tableName={tableName}
                      fieldName={column}
                      symbol={schema.properties[column]?.symbol}
                    />
                  );
                })}
            </Fragment>
          ))}
    </>
  );
};

const renderSimpleTable = ({
  columns,
  schema,
  columnValues,
  onCellChange,
  levelIndex,
  level,
  name,
  children,
  dataIndex,
  cellRefs,
  onCellKeyDown,
  keyPrefix,
  questionName,
  fund,
  tableName,
  subGroupColumn,
  commonColumn,
  groupColumnValue
}) => {
  const columnSize = Object.keys(columns).length;
  return (
    <>
      {dataIndex === 0 && <SingleFundHeaderRow levelIndex={0} name={questionName} />}
      {columnSize > 1 && (
        <SingleFundHeaderRow
          levelIndex={questionName ? levelIndex + 1 : levelIndex}
          level={level}
          name={name}
        />
      )}

      {commonColumn && (
        <SingleFundRow
          columnSchema={columns[commonColumn]}
          cellSchema={schema.properties[commonColumn]}
          label={columns[commonColumn].name}
          value={columnValues?.[0]?.[commonColumn]}
          onChange={value => onCellChange(commonColumn, value, null, groupColumnValue, null)}
          cellRefs={cellRefs}
          cellKey={`${keyPrefix}-${commonColumn}-${groupColumnValue}`}
          onCellKeyDown={onCellKeyDown}
          showIndent={Boolean(level) || questionName}
          columnLevel={columnSize === 1 ? level : undefined}
          fundId={fund?.fund_id}
          fundName={fund?.fund_name}
          tableName={tableName}
          fieldName={commonColumn}
          symbol={schema.properties[commonColumn]?.symbol}
        />
      )}

      {!children &&
        schema.properties[subGroupColumn]?.rules?.array?.enum
          ?.filter(item => item.value.includes(groupColumnValue))
          ?.map(item => (
            <Fragment key={item.value}>
              {Object.keys(columns)
                .filter(column => column !== commonColumn)
                .map(column => {
                  const row = columnValues.find(row => row[subGroupColumn] === item.value);
                  return (
                    <SingleFundRow
                      key={column}
                      columnSchema={columns[column]}
                      cellSchema={schema.properties[column]}
                      label={`${item.label} (${columns[column].name})`}
                      levelIndex={level && columnSize > 1 ? levelIndex + 1 : levelIndex}
                      value={row?.[column]}
                      error={row?.errors?.[column]}
                      onChange={value =>
                        onCellChange(column, value, null, groupColumnValue, item.value)
                      }
                      cellRefs={cellRefs}
                      cellKey={`${keyPrefix}-${column}-${groupColumnValue}-${item.value}`}
                      onCellKeyDown={onCellKeyDown}
                      showIndent={Boolean(level) || questionName}
                      columnLevel={columnSize === 1 ? level : undefined}
                      fundId={fund?.fund_id}
                      fundName={fund?.fund_name}
                      tableName={tableName}
                      fieldName={column}
                      hasDrillDownFeature={schema.properties[column]?.has_drill_down_feature}
                      tableId={row?.['id']}
                      reportId={row?.['report_id']}
                      isFromStaticTemplate={row?.['static_template_fields']?.includes(column)}
                      symbol={schema.properties[column]?.symbol}
                    />
                  );
                })}
            </Fragment>
          ))}
    </>
  );
};
